import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiServices from "../../service";

interface ProcessedFile {
  name: string;
  path: string;
  lastModified: string;
  metadata: any;
  type: "file" | "folder";
}

interface DocumentState {
  fileList: ProcessedFile[];
  loading: boolean;
  error: string | null;
  basePath: string;
  currentPath: string[];
}

const initialState: DocumentState = {
  fileList: [],
  loading: false,
  error: null,
  basePath: "",
  currentPath: [],
};

export const fetchDocuments = createAsyncThunk<
  { files: ProcessedFile[]; basePath: string },
  string,
  { rejectValue: string }
>("documents/fetchAll", async (customerId, { rejectWithValue }) => {
  try {
    const res = await apiServices.clientDocument.getAllList(customerId);
    const rawData: any[] = Array.isArray(res.data.filteredData) ? res.data.filteredData : [];
    let basePath = "";
    if (rawData.length > 0 && rawData[0]?.Key) {
      basePath = rawData[0].Key.split("/")[1] ?? "";
    }
    const files: ProcessedFile[] = rawData.map((file: any) => {
      const isFolder = file.Key.endsWith("/");
      const parts = file.Key.split("/").filter(Boolean);
      const rawPath = parts.slice(2).join("/");
      const relativePath = isFolder ? `${rawPath}/` : rawPath;
      return {
        name: parts[parts.length - 1],
        path: relativePath,
        lastModified: file.LastModified,
        metadata: file.Metadata || {},
        type: isFolder ? "folder" : "file",
      };
    });
    return { files, basePath };
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch documents"
    );
  }
});

const documentSlice = createSlice({
  name: "documents",
  initialState,
  reducers: {
    setCurrentPath(state, action: PayloadAction<string[]>) {
      state.currentPath = action.payload;
    },
    resetDocuments(state) {
      state.fileList = [];
      state.currentPath = [];
      state.basePath = "";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.fileList = action.payload.files;
        state.basePath = action.payload.basePath;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      });
  },
});

export const { setCurrentPath, resetDocuments } = documentSlice.actions;
export default documentSlice.reducer;
